import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import { useGetPointsPerUserQuery } from 'store/services/admin/analyticsApi';
import { AnalyticsPeriod, PointsPerUserDataPoint } from 'types/interfaces/admin/analytics';
import TimeRangeDropdown from './TimeRangeDropdown';

interface Props {
  period: AnalyticsPeriod;
  onPeriodChange: (period: AnalyticsPeriod) => void;
}

const CHART_COLOR = '#A855F7';

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) return null;
  const dataPoint: PointsPerUserDataPoint = payload[0]?.payload;
  return (
    <div style={{
      backgroundColor: '#1a1a1a',
      border: '1px solid #2a2a2a',
      borderRadius: '8px',
      padding: '10px 14px',
      color: '#fff',
      fontSize: '13px'
    }}>
      <div style={{ marginBottom: '6px', fontWeight: 500 }}>
        {formatDate(dataPoint.date)}: {Math.round(dataPoint.pointsPerUser).toLocaleString()} points/user
      </div>
      <div style={{ color: '#999' }}>
        {dataPoint.totalPoints.toLocaleString()} total ÷ {dataPoint.activeUsers.toLocaleString()} active users
      </div>
    </div>
  );
};

const PointsPerUserChart: React.FC<Props> = ({ period, onPeriodChange }) => {
  const { data, isLoading, error } = useGetPointsPerUserQuery({ period });

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div className="chart-header-left">
          <h2>Points Per Active User</h2>
          <p className="chart-subtitle">Total points ÷ active users (logged in within 30 days)</p>
        </div>
        <div className="chart-header-right">
          <div className="chart-legend">
            <span className="legend-dot points-per-user" />
            <span className="legend-label">Points/User</span>
          </div>
          <TimeRangeDropdown value={period} onChange={onPeriodChange} />
        </div>
      </div>

      <div className="chart-container">
        {isLoading ? (
          <div className="chart-loading">Loading...</div>
        ) : error ? (
          <div className="chart-error">Failed to load data</div>
        ) : data ? (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <defs>
                <linearGradient id="pointsPerUserGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLOR} stopOpacity={0.15} />
                  <stop offset="95%" stopColor={CHART_COLOR} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                stroke="#666"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#2a2a2a' }}
                tickLine={false}
                interval={data.data.length > 60 ? Math.floor(data.data.length / 12) : 0}
              />
              <YAxis
                stroke="#666"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#2a2a2a' }}
                tickLine={false}
                tickFormatter={(value) => Math.round(value).toLocaleString()}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="pointsPerUser"
                stroke={CHART_COLOR}
                strokeWidth={2}
                fill="url(#pointsPerUserGradient)"
                name="Points/User"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : null}
      </div>

      {data && (
        <div className="chart-summary">
          Current: {Math.round(data.currentPointsPerUser).toLocaleString()} points/user ({data.currentTotalPoints.toLocaleString()} total ÷ {data.currentActiveUsers.toLocaleString()} active users)
        </div>
      )}
    </div>
  );
};

export default PointsPerUserChart;
